/** Material detail + lifecycle edits (priority / deprecate / supersede — the
 * three verbs the plan names) and the list of proposals this material fed.
 * No delete: materials are provenance; deprecation is the end-state. */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as materialsApi from '@/api/materials';
import type { MaterialListItem, MaterialUpdate } from '@/api/materials';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import {
  designImportsHref,
  materialDeprecatedActions,
  materialKindActions,
} from '@/features/notifications/notificationLinks';

import { ImportDesignWizard } from './ImportDesignWizard';

interface Props {
  material: MaterialListItem | null;
  onClose: () => void;
  canManage: boolean;
}

export function MaterialDetailDialog({ material, onClose, canManage }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('0');
  const [deprecated, setDeprecated] = useState(false);
  const [supersedes, setSupersedes] = useState('');
  const [reingestOpen, setReingestOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['material', material?.id],
    queryFn: () => materialsApi.retrieve(material!.id),
    enabled: material !== null,
  });

  useEffect(() => {
    if (detail.data) {
      setTitle(detail.data.title);
      setDescription(detail.data.description);
      setPriority(String(detail.data.priority));
      setDeprecated(detail.data.deprecated);
      setSupersedes(detail.data.supersedes ? String(detail.data.supersedes) : '');
    }
  }, [detail.data]);

  const mutation = useMutation({
    mutationFn: (body: MaterialUpdate) => materialsApi.update(material!.id, body),
    onSuccess: () => {
      snackbar.show('Material updated.', 'success');
      qc.invalidateQueries({ queryKey: ['materials'] });
      qc.invalidateQueries({ queryKey: ['material', material?.id] });
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const save = () => {
    const body: MaterialUpdate = {
      title: title.trim(),
      description,
      priority: /^-?\d+$/.test(priority) ? Number(priority) : 0,
      deprecated,
      supersedes: /^\d+$/.test(supersedes) ? Number(supersedes) : null,
    };
    mutation.mutate(body);
  };

  if (!material) return null;
  const data = detail.data;

  return (
    <AppDialog
      open
      onClose={onClose}
      size="md"
      title={`Material #${material.id} — ${material.title}`}
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          {canManage && (
            <Button variant="contained" onClick={save} disabled={mutation.isPending || !title.trim()}>
              Save
            </Button>
          )}
        </>
      }
    >
      {detail.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {data && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <ValueActions chip actions={materialKindActions(data.kind)}>
              <Chip size="small" variant="outlined" label={data.kind} />
            </ValueActions>
            <ValueActions
              chip
              actions={operatorActions(data.tour_operator, {
                href: designImportsHref({ tenant: data.tour_operator.id }),
                label: 'Materials of this operator',
              })}
            >
              <Chip
                size="small"
                variant="outlined"
                label={data.tour_operator.code || data.tour_operator.name}
              />
            </ValueActions>
            {data.deprecated && (
              <ValueActions chip actions={materialDeprecatedActions(true)}>
                <Chip size="small" color="warning" label="deprecated" />
              </ValueActions>
            )}
            {data.url && (
              <Link href={data.url} target="_blank" rel="noreferrer" variant="body2">
                {data.url}
              </Link>
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1.5, mb: 1.5 }}>
            <TextField size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <TextField
              size="small"
              label="Priority (higher wins)"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </Box>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Supersedes material #"
              value={supersedes}
              onChange={(e) => setSupersedes(e.target.value)}
              sx={{ width: 200 }}
              helperText="The v1 this document replaces."
            />
            <FormControlLabel
              control={<Switch checked={deprecated} onChange={(e) => setDeprecated(e.target.checked)} />}
              label="Deprecated"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Proposals from this material
          </Typography>
          {data.proposals.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No proposals yet — run an import to get a reviewable draft.
            </Alert>
          ) : (
            <Box sx={{ mb: 2 }}>
              {data.proposals.map((p) => (
                <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                  <Link component={RouterLink} to={`/notifications/design-imports/proposals/${p.id}`} variant="body2">
                    Proposal #{p.id}
                  </Link>
                  <Chip size="small" variant="outlined" label={p.status} />
                  <Typography variant="caption" color="text.secondary">
                    {p.origin} · {new Date(p.created_ts).toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {canManage && !data.deprecated && (
            <Button variant="outlined" size="small" onClick={() => setReingestOpen(true)}>
              Import again from this material
            </Button>
          )}
          <ImportDesignWizard
            open={reingestOpen}
            onClose={() => setReingestOpen(false)}
            material={data}
          />
        </>
      )}
    </AppDialog>
  );
}
